import { getHourse, createHourse,deleteHourse, updateHourse } from "../../Utils/apiServices";
import { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  InputAdornment,
  Chip,
  Typography,
  IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import Snackbar from "../../Components/Snackbar";
import { useNavigate } from 'react-router-dom';
import { calculateAge } from "../../Utils/helper";
import ConfirmDialog from "../../Components/ConfirmDialog";

const Horse = () => {
  const navigate = useNavigate();
  const [horses, setHorses] = useState([]);
  const [filteredHorses, setFilteredHorses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    message: '',
    isOpen: false,
    type: 'success'
  });
  const [newHorse, setNewHorse] = useState({
    name: '',
    breed: '',
    dob: '',
    horse_id: '',
    gender: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [selectedHorse, setSelectedHorse] = useState(null);
  const [deleteHorseId, setDeleteHorseId] = useState(null);
  const [deleteHorseDialogOpen, setDeleteHorseDialogOpen] = useState(false);
  const [deleteHorseLoading, setDeleteHorseLoading] = useState(false);

  useEffect(() => {
    getHourse().then((response) => {
      setHorses(response.data.data);
      setFilteredHorses(response.data.data);
    });
  }, []);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleSearch = (event) => {
    const term = event.target.value.toLowerCase();
    setSearchTerm(term);
    
    const filtered = horses.filter(horse => 
      (horse.name?.toLowerCase() || '').includes(term) ||
      (horse.breed?.toLowerCase() || '').includes(term) ||
      (horse.horse_id?.toLowerCase() || '').includes(term) ||
      (horse.gender?.toLowerCase() || '').includes(term)
    );
    
    setFilteredHorses(filtered);
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setNewHorse({
      ...newHorse,
      [e.target.name]: value
    });
  };


  const handleGenderChange = (e) => {
    setNewHorse({
      ...newHorse,
      gender: e.target.value
    });
  };

  const handleEdit = (horse) => {
    setSelectedHorse(horse);
    setNewHorse(horse);
    setIsEditing(true);
    setOpen(true);
  };

  const openDeleteHorseConfirm = (id) => {
    setDeleteHorseId(id);
    setDeleteHorseDialogOpen(true);
  };

  const closeDeleteHorseConfirm = () => {
    if (deleteHorseLoading) return;
    setDeleteHorseDialogOpen(false);
    setDeleteHorseId(null);
  };

  const confirmDeleteHorse = async () => {
    if (deleteHorseId == null) return;
    setDeleteHorseLoading(true);
    try {
      const deletePayload = { id: deleteHorseId };
      const response = await deleteHourse(deletePayload);
      if (response.status === 200) {
        setSnackbar({
          message: 'Horse deleted successfully',
          isOpen: true,
          type: 'success'
        });
        setHorses(horses.filter(horse => horse.id !== deleteHorseId));
        setDeleteHorseDialogOpen(false);
        setDeleteHorseId(null);
      }
    } catch (error) {
      setSnackbar({
        message: 'Error deleting horse',
        isOpen: true,
        type: 'error'
      });
      console.error('Error deleting horse:', error);
    } finally {
      setDeleteHorseLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const payload = Object.entries(newHorse).reduce((acc, [key, value]) => {
        if (value !== '') {
          acc[key] = value;
        }
        return acc;
      }, {});

      if (isEditing) {
        const response = await updateHourse(selectedHorse.id, payload);
        if (response.data.success) {
          const refreshResponse = await getHourse();
          setHorses(refreshResponse.data.data);
          
          setSnackbar({
            message: 'Horse updated successfully',
            isOpen: true,
            type: 'success'
          });
          setIsEditing(false);
        }
      } else {
        const response = await createHourse(payload);
        if (response.status === 200) {
          setHorses(prevHorses => [...prevHorses, response.data.data]);
          setSnackbar({
            message: 'Horse created successfully',
            isOpen: true,
            type: 'success'
          });
        }
      }
      handleClose();
      setNewHorse({
        name: '',
        breed: '',
        dob: '',
        horse_id: '',
        gender: ''
      });
    } catch (error) {
      setSnackbar({
        message: 'Error saving horse',
        isOpen: true,
        type: 'error'
      });
      console.error('Error saving horse:', error);
    }
  };

  const handleNameClick = (horseId) => {
    navigate(`/horse/details/${horseId}`);
  };

  return (
    <Box>
      {/* Colorful page banner */}
      <Box
        sx={{
          mb: 3,
          p: 2.5,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #10b981 0%, #0ea5e9 50%, #3b82f6 100%)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 8px 24px rgba(16,185,129,0.3)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -40,
            right: -40,
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
          },
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.3 }}>🐎 Horse Management</Typography>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>Browse, create and manage all horses in the system</Typography>
        </Box>
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1 }}>{horses.length}</Typography>
          <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.7rem', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Total Horses</Typography>
        </Box>
      </Box>

      <ConfirmDialog
        open={deleteHorseDialogOpen}
        title="Delete horse"
        message="Are you sure you want to delete this horse? This action cannot be undone."
        confirmLabel="Delete"
        onCancel={closeDeleteHorseConfirm}
        onConfirm={confirmDeleteHorse}
        loading={deleteHorseLoading}
      />

      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
          <TextField
            placeholder="Search by name, breed, ID, or gender..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearch}
            sx={{ maxWidth: { sm: 380 } }}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            onClick={handleOpen}
            startIcon={<AddIcon />}
            sx={{ flexShrink: 0 }}
          >
            Add Horse
          </Button>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          Showing {filteredHorses.length} of {horses.length} horses
        </Typography>
      </Paper>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{isEditing ? 'Edit Horse' : 'Create New Horse'}</DialogTitle>
        <DialogContent>
          <TextField
            name="name"
            label="Name"
            fullWidth
            margin="dense"
            value={newHorse.name}
            onChange={handleChange}
          />
          <TextField
            name="breed"
            label="Breed"
            fullWidth
            margin="dense"
            value={newHorse.breed}
            onChange={handleChange}
          />
          <TextField
            name="dob"
            label="Date of Birth"
            type="date"
            fullWidth
            margin="dense"
            value={newHorse.dob}
            onChange={handleChange}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            name="horse_id"
            label="Horse ID"
            fullWidth
            margin="dense"
            value={newHorse.horse_id}
            onChange={handleChange}
          />
          <TextField
            select
            name="gender"
            label="Gender"
            fullWidth
            margin="dense"
            value={newHorse.gender}
            onChange={handleGenderChange}
            SelectProps={{
              native: true,
            }}
          >
            <option value="">Select Gender</option>
            <option value="stallion">Stallion</option>
            <option value="gelded">Gelded</option>
            <option value="mare">Mare</option>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow
              sx={{
                '& .MuiTableCell-head': {
                  bgcolor: 'grey.50',
                  color: 'text.secondary',
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  letterSpacing: '0.02em',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  py: 1.5,
                },
              }}
            >
              <TableCell>ID</TableCell>
              <TableCell>Horse ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Breed</TableCell>
              <TableCell>Age</TableCell>
              <TableCell>Gender</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredHorses.map((horse) => (
              <TableRow
                key={horse.id}
                hover
                sx={{ '&:last-child td': { borderBottom: 0 }, transition: 'background-color 0.15s' }}
              >
                <TableCell sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>{horse.id}</TableCell>
                <TableCell sx={{ fontWeight: 500 }}>{horse.horse_id}</TableCell>
                <TableCell>
                  <Button
                    onClick={() => handleNameClick(horse.id)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      color: 'primary.main',
                      p: 0,
                      minWidth: 0,
                      textAlign: 'left',
                    }}
                  >
                    {horse.name}
                  </Button>
                </TableCell>
                <TableCell sx={{ color: 'text.secondary' }}>{horse.breed}</TableCell>
                <TableCell sx={{ color: 'text.secondary' }}>{calculateAge(horse.dob)}</TableCell>
                <TableCell>
                  <Chip label={horse.gender || '—'} size="small" variant="outlined" sx={{ fontWeight: 500, textTransform: 'capitalize' }} />
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    <IconButton
                      onClick={() => handleEdit(horse)}
                      color="primary"
                      size="small"
                      sx={{ border: '1px solid', borderColor: 'divider' }}
                      aria-label="Edit horse"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      onClick={() => openDeleteHorseConfirm(horse.id)}
                      color="error"
                      size="small"
                      sx={{ border: '1px solid', borderColor: 'divider' }}
                      aria-label="Delete horse"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Snackbar 
        message={snackbar.message}
        isOpen={snackbar.isOpen}
        type={snackbar.type}
      />
    </Box>
  );
};

export default Horse;